//Navigation.js
import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { Navbar, Nav, Container, Image, Button, Dropdown } from 'react-bootstrap';
import DashboardAccessModal from '../common/DashboardAccessModal';
// import ComingSoonModal from '../common/ComingSoonModal';
import { getAdminConfigService } from '../../Services/AdminConfigService';
import useDashboardSignature from '../../hooks/useDashboardSignature';
import logo from '../../assets/app-logo.png';
import '../Home/HomePage.css';
import './navigation.css';

const getInitials = (user) => {
    if (!user || !user.email) return '';
    const email = user.email;
    const emailName = email.split('@')[0];
    if (emailName.length <= 2) {
        return emailName.toUpperCase();
    } else {
        return (emailName[0] + emailName[emailName.length - 1]).toUpperCase();
    }
};

const Navigation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(AuthContext);
    const [showDashboardModal, setShowDashboardModal] = useState(false);
    const [isDashboardEnabled, setIsDashboardEnabled] = useState(false);
    const [loadingDashboardConfig, setLoadingDashboardConfig] = useState(true);

    // Custom hook for dashboard signature generation
    const { generateSignedUrl, loading: signatureLoading, error: signatureError, clearError } = useDashboardSignature();

    // COMMENTED OUT: External dashboard link for logged-in users - OLD IMPLEMENTATION
    // const accessToken = user && user.firebaseUser ? user.firebaseUser.accessToken : 'demoToken';
    // const uid = user && user.firebaseUser ? user.firebaseUser.uid : 'demoUid';
    // const dashboardExternalUrl = `https://invest.rightspade.com/user?accessToken=${encodeURIComponent(accessToken)}&uid=${encodeURIComponent(uid)}`;

    const handleDashboardClick = async (e) => {
        e.preventDefault();
        console.log('user.status', user.status);
        console.log('isDashboardEnabled', isDashboardEnabled);

        if (user.status.toLowerCase() === "approved") {
            if (isDashboardEnabled) {
                try {
                    // Generate signed URL for dashboard access
                    console.log('🔐 Generating signed dashboard URL...');
                    const signedUrl = 'https://invest.rightspade.com'
                    // const signedUrl = await generateSignedUrl({
                    //     uid: user.uid || user.firebaseUser?.uid,
                    //     email: user.email,
                    //     status: user.status,
                    //     role: user.role,
                    //     permissions: user.permissions || []
                    // });

                    console.log('✅ Signed URL generated, redirecting to:', signedUrl);

                    // Redirect to external dashboard with signed URL
                    window.location.href = signedUrl;

                } catch (error) {
                    console.error('❌ Failed to generate dashboard signature:', error);
                    // Show error modal or alert
                    alert(`Dashboard access error: ${error.message}`);
                }
            } else {
                // Show modal if dashboard is disabled
                setShowDashboardModal(true);
            }
        } else {
            setShowDashboardModal(true);
        }
    };

    // Load dashboard configuration
    useEffect(() => {
        const loadDashboardConfig = async () => {
            console.log("user.status", user.status);
            console.log("isDashboardEnabled", isDashboardEnabled);
            try {
                const adminConfigService = getAdminConfigService();
                const enabled = await adminConfigService.isDashboardEnabled();
                setIsDashboardEnabled(enabled);
            } catch (error) {
                console.error('Error loading dashboard config:', error);
                setIsDashboardEnabled(false);
            } finally {
                setLoadingDashboardConfig(false);
            }
        };

        if (user) {
            loadDashboardConfig();
        }
    }, [user]);

    const handleCloseDashboardModal = () => setShowDashboardModal(false);

    // Primary nav links (desktop bar)
    const loggedInLinks = [
        { label: 'Right Report',   to: '/dashboard/reports' },
        { label: 'Right Now',      to: '/dashboard/market-overview' },
        { label: 'Right Snippets', to: '/dashboard/market-wrap' },
        // { label: '🤖 AI', to: '/dashboard/ai-assistant' },
    ];

    // Compliance links shown directly in nav (SEBI regulatory requirement)
    const complianceLinks = [
        { label: 'Investor Charter', to: '/legal#investor-charter' },
        { label: 'Complaints Data', to: '/legal#complaints-data' },
    ];

    // Helper to get photoURL from user or user.firebaseUser
    const getPhotoURL = (user) => {
        if (!user) return null;
        return user.photoURL || (user.firebaseUser && user.firebaseUser.photoURL) || null;
    };

    return (
        <>
            <header>
            <Navbar bg="white" expand="md" className={`navbar-homepage fixed-top py-4${user ? ' navbar-logged-in' : ''}`} aria-label="Primary navigation">
                <Container className="d-flex justify-content-between align-items-center">
                    {/* Logo always shown — auditor (docx round-2): "There is no way to
                        go back to the homepage once you enter another page." */}
                    <Navbar.Brand as={Link} to="/" className="me-auto">
                        {/* alt describes the link destination (auditor wording) */}
                        <Image src={logo} alt="Right Spade Home" />
                    </Navbar.Brand>
                    <Navbar.Toggle aria-controls="basic-navbar-nav" />
                    <Navbar.Collapse id="basic-navbar-nav">
                        {user ? (
                            <div className="navbar-flex-main w-100 d-flex align-items-center justify-content-between">
                                {/* Left group: primary nav links + compliance links */}
                                <Nav className="nav-menu-group">
                                    {loggedInLinks.map(link => (
                                        <Nav.Link
                                            as={Link}
                                            to={link.to}
                                            key={link.to}
                                            className={location.pathname === link.to ? 'active' : ''}
                                        >
                                            {link.label}
                                        </Nav.Link>
                                    ))}
                                    {/* Separator between primary and compliance links */}
                                    <span className="nav-compliance-divider d-none d-md-block" />
                                    {/* Compliance links shown directly (SEBI regulatory requirement) */}
                                    {complianceLinks.map(link => (
                                        <Nav.Link
                                            as={Link}
                                            to={link.to}
                                            key={link.to}
                                            className={location.pathname === link.to ? 'active' : ''}
                                        >
                                            {link.label}
                                        </Nav.Link>
                                    ))}
                                </Nav>
                                {/* Right group: Dashboard button and avatar */}
                                <div className="nav-right-group d-flex align-items-center ms-auto">
                                    {/* FUTURE IMPLEMENTATION: Uncomment below to restore external dashboard link */}
                                    {/* <a
                                        href={user.status === "Approved" ? dashboardExternalUrl : undefined}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-primary ms-3 d-flex align-items-center dashboard-btn-nav"
                                        style={{ fontWeight: 600 }}
                                        onClick={e => {
                                            if (user.status !== "Approved") {
                                                e.preventDefault();
                                                alert("Access restricted to subscribers. Please upgrade your plan to view this content.");
                                            }
                                        }}
                                    >
                                        <span className="me-2" style={{ fontSize: '1.1em' }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="white" viewBox="0 0 16 16" style={{ marginRight: '4px', marginBottom: '2px' }}>
                                                <path d="M8 2l2.09 4.26L15 6.27l-3.5 3.41L12.18 14 8 11.77 3.82 14l.68-4.32L1 6.27l4.91-.01L8 2z"/>
                                            </svg>
                                            Dashboard
                                        </span>
                                    </a> */}

                                    <Button
                                        variant="primary"
                                        className="ms-3 d-flex align-items-center dashboard-btn-nav"
                                        style={{ fontWeight: 600 }}
                                        onClick={handleDashboardClick}
                                        disabled={signatureLoading}
                                    >
                                        <span className="flex items-center text-white text-base font-medium">
                                            {signatureLoading ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Generating...
                                                </>
                                            ) : (
                                                <>
                                                    {/* Decorative star — hidden from AT, button text carries the name (docx round-2) */}
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="white" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                                                        <path d="M8 2l2.09 4.26L15 6.27l-3.5 3.41L12.18 14 8 11.77 3.82 14l.68-4.32L1 6.27l4.91-.01L8 2z" />
                                                    </svg>
                                                    Dashboard
                                                </>
                                            )}
                                        </span>
                                    </Button>
                                    <Dropdown align="end" className="profile-dropdown ms-3">
                                        <Dropdown.Toggle as="div" id="profile-dropdown-toggle" className="profile-avatar-toggle">
                                            {getPhotoURL(user) ? (
                                                <img
                                                    src={getPhotoURL(user)}
                                                    alt="Profile"
                                                    className="profile-avatar-img"
                                                    title={user.email || 'My Profile'}
                                                    referrerPolicy="no-referrer"
                                                />
                                            ) : (
                                                <div className="profile-avatar" title={user.email || 'My Profile'}>
                                                    {getInitials(user)}
                                                </div>
                                            )}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Header>{user.email}</Dropdown.Header>
                                            <Dropdown.Divider />
                                            {user.role === 'admin' && (
                                                <Dropdown.Item as={Link} to="/admin">Admin</Dropdown.Item>
                                            )}
                                            <Dropdown.Item as={Link} to="/logout">Logout</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </div>
                        ) : (
                            <Nav className="ms-auto top-nav-items align-items-center w-100 justify-content-end">
                                <Nav.Link as={Link} to="/legal#investor-charter">Investor Charter</Nav.Link>
                                <Nav.Link as={Link} to="/legal#complaints-data">Complaints Data</Nav.Link>
                                <Nav.Link as={Link} to="/contact">About</Nav.Link>
                                <Button as={Link} to="/login" variant="primary" className="sign-in-button ms-2">Sign In</Button>
                            </Nav>
                        )}
                    </Navbar.Collapse>
                </Container>
            </Navbar>
            </header>

            {/* Dashboard Access Modal */}
            <DashboardAccessModal
                show={showDashboardModal}
                onHide={handleCloseDashboardModal}
            />

            {/* Coming Soon Modal - COMMENTED OUT FOR FUTURE USE */}
            {/* <ComingSoonModal
                show={showDashboardModal}
                onHide={handleCloseDashboardModal}
                title="Enhanced Dashboard for Research Clients"
                icon="🔧"
                description="We're building a powerful new experience to help you track equity research recommendations and make informed investment decisions — with greater transparency and control."
                features={[
                    "Advanced Analytics Dashboard",
                    "Real-time Market Data Integration",
                    "Performance Tracking of Research Calls"
                ]}
                disclaimer="For actionable insights, please refer to our SEBI-compliant published research reports. Investing involves risk. Past performance is not indicative of future results. Terms and Conditions apply. Please review the Investor Charter."
                primaryButtonText="Go to Market Overview"
                primaryButtonAction="/dashboard/market-overview"
            /> */}
        </>
    );
};

export default Navigation;
